import type { ActionProperties, EnvironmentV2Resource, GitDependencyReference, GitRefResource, IProcessResource, PackageReference, ProcessTemplateParameterResource, StepPackageInputs, TriggerResource } from "@octopusdeploy/octopus-server-client";
import { ProcessType } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import type * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useAccountsFromContext, useRefreshAccountsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessAccountsContextProvider";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { useGitCredentialsFromContext, useRefreshGitCredentialsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { useGitHubConnectionsFromContext, useRefreshGitHubConnectionsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitHubConnectionsContextProvider";
import { useOptionalProducerTemplateProcessContext } from "~/areas/projects/components/Process/ProjectTemplates/useProducerTemplateProcessContext";
import type { RunOn } from "~/areas/projects/components/Process/types";
import { useOptionalProjectContext } from "~/areas/projects/context";
import type { ActionInputsDependencies } from "~/components/ActionInputs/ActionInputs";
import type { ProcessTemplateActionInputsDependencies } from "~/components/ActionInputs/ProcessTemplateActionInputs";
import type { ProjectActionInputsDependencies } from "~/components/ActionInputs/ProjectActionInputs";
import type { ProjectTemplateActionInputsDependencies } from "~/components/ActionInputs/ProjectTemplateActionInputs";
import { ActionPropertiesEditor } from "~/components/ActionPropertiesEditor/ActionPropertiesEditor";
import { enforceNewActionFeatures } from "~/components/ActionPropertiesEditor/enforceNewActionFeatures";
import type { ActionEditProps, ActionPlugin, AdditionalActions } from "~/components/Actions/pluginRegistry";
import FeatureEditor from "~/components/FeatureEditor/FeatureEditor";
import type { ProjectStepInputDependencies } from "~/components/StepPackageEditor/StepInputDependencies";
import type { FieldErrors } from "../DataBaseComponent/Errors";
interface ProjectActionPropertiesEditorAndFeaturesProps {
    deploymentActionSlug: string;
    plugin: ActionPlugin;
    inputs: StepPackageInputs;
    properties: ActionProperties;
    packages: Array<PackageReference>;
    gitDependencies?: GitDependencyReference[];
    environments: EnvironmentV2Resource[];
    runOn: RunOn;
    additionalActions: AdditionalActions;
    errors: FieldErrors | undefined; // only used for shouldComponentUpdate
    busy: Promise<unknown> | boolean | undefined;
    expandedByDefault: boolean;
    editor: React.ComponentType<ActionEditProps>;
    getFieldError(field: string): string;
    setInputs(inputs: StepPackageInputs, callback?: () => void): void;
    setProperties(properties: Partial<ActionProperties>, initialise?: boolean, callback?: () => void): void;
    setPackages(packages: Array<PackageReference>, initialise?: boolean): void;
    setGitDependencies?(gitDependencies: GitDependencyReference[], initialise?: boolean): void;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
    refreshRunOn(): void;
    getProcessResource(): Readonly<IProcessResource>;
    isNew: boolean;
    projectTriggers: TriggerResource[];
    gitRefResource: GitRefResource | undefined;
    actionHasFeatures: boolean;
    enabledFeatures: string;
    openFeaturesElement?: JSX.Element;
    projectId: string | undefined;
    onParameterAdded?: (parameters: ProcessTemplateParameterResource[]) => void | undefined;
}
export function ProjectActionPropertiesEditorAndFeatures(props: ProjectActionPropertiesEditorAndFeaturesProps) {
    const { setPackages, packages } = props;
    const { setGitDependencies, gitDependencies } = props;
    const { gitRefResource: gitRef } = props;
    const projectContext = useOptionalProjectContext();
    const processContext = useProcessContext();
    useEffect(() => {
        const properties = { ...props.properties };
        const newActionFeatures = enforceNewActionFeatures(props.plugin, properties, props.isNew, false);
        if (newActionFeatures.length > 0) {
            // We don't assign empty features, or this causes existing steps to trigger the "unsaved changes" dialog.
            properties["Octopus.Action.EnabledFeatures"] = newActionFeatures;
        }
        props.setProperties(properties, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const projectId = projectContext?.state.model.Id;
    const project = projectContext?.state.model;
    const processType = processContext.selectors.getProcessType();
    const feeds = useFeedsFromContext();
    const environments = props.environments;
    const refreshFeeds = useRefreshFeedsFromContext();
    const accounts = useAccountsFromContext();
    const refreshAccounts = useRefreshAccountsFromContext();
    const gitCredentials = useGitCredentialsFromContext();
    const refreshGitCredentials = useRefreshGitCredentialsFromContext();
    const gitHubConnections = useGitHubConnectionsFromContext();
    const refreshGitHubConnections = useRefreshGitHubConnectionsFromContext();
    const stableSetPackages = useCallback((getUpdatedPackages: (prev: Array<PackageReference>) => Array<PackageReference>) => setPackages(getUpdatedPackages(packages)), [packages, setPackages]);
    const stableSetGitDependencies = useCallback((getGitDependencies: (prev: GitDependencyReference[]) => GitDependencyReference[]) => setGitDependencies?.(getGitDependencies?.(gitDependencies ?? [])), [gitDependencies, setGitDependencies]);
    const stableRefreshFeeds = useCallback(async () => {
        await refreshFeeds();
    }, [refreshFeeds]);
    const stableRefreshAccounts = useCallback(async () => {
        await refreshAccounts();
    }, [refreshAccounts]);
    const inputDependencies = useMemo<ProjectStepInputDependencies>(() => ({
        projectId,
        gitRef,
        project,
        processType,
        feeds,
        refreshFeeds: stableRefreshFeeds,
        setPackages: stableSetPackages,
        setGitDependencies: stableSetGitDependencies,
        accounts,
        refreshAccounts: stableRefreshAccounts,
        gitCredentials: gitCredentials,
        refreshGitCredentials: refreshGitCredentials,
        gitHubConnections: gitHubConnections,
        refreshGitHubConnections: refreshGitHubConnections,
        deploymentActionSlug: props.deploymentActionSlug,
        projectTriggers: props.projectTriggers,
    }), [
        projectId,
        gitRef,
        project,
        processType,
        feeds,
        stableRefreshFeeds,
        stableSetPackages,
        stableSetGitDependencies,
        accounts,
        stableRefreshAccounts,
        gitCredentials,
        refreshGitCredentials,
        gitHubConnections,
        refreshGitHubConnections,
        props.deploymentActionSlug,
        props.projectTriggers,
    ]);
    if (!props.plugin.editSections) {
        throw new Error(`Plugin ${props.plugin.actionType} is missing 'Edit' property`);
    }
    const producerTemplateProcessContext = useOptionalProducerTemplateProcessContext();
    const localNames = producerTemplateProcessContext?.localNames ? producerTemplateProcessContext.localNames : [];
    const processTemplateActionInputsDependencies: ProcessTemplateActionInputsDependencies = {
        type: "process-template",
        setPackages: props.setPackages,
        expandedByDefault: props.expandedByDefault,
        parameters: producerTemplateProcessContext?.parameters || [],
        packages: props.packages,
        modelDirty: !isEqual(processContext?.state.model, processContext?.state.cleanModel),
        getFieldError: props.getFieldError,
        setProperties: props.setProperties,
        properties: props.properties,
        runOn: props.runOn,
        localNames,
        onParameterAdded: props.onParameterAdded,
    };
    const projectActionInputsDependencies: ProjectActionInputsDependencies = {
        type: "project",
        setPackages: props.setPackages,
        expandedByDefault: props.expandedByDefault,
        projectId: inputDependencies.projectId,
        gitRef: inputDependencies.gitRef,
        packages: props.packages,
        modelDirty: !isEqual(processContext?.state.model, processContext?.state.cleanModel),
        feeds: inputDependencies.feeds,
        environments: props.environments,
        refreshFeeds: inputDependencies.refreshFeeds,
        stepInputDependencies: inputDependencies,
        getFieldError: props.getFieldError,
        properties: props.properties,
        setProperties: props.setProperties,
        runOn: props.runOn,
        doBusyTask: props.doBusyTask,
    };
    const projectTemplateActionInputsDependencies: ProjectTemplateActionInputsDependencies = {
        type: "project-template",
        setPackages: props.setPackages,
        expandedByDefault: props.expandedByDefault,
        parameters: producerTemplateProcessContext?.parameters || [],
        packages: props.packages,
        modelDirty: !isEqual(processContext?.state.model, processContext?.state.cleanModel),
        getFieldError: props.getFieldError,
        setProperties: props.setProperties,
        properties: props.properties,
        runOn: props.runOn,
        localNames,
        onParameterAdded: props.onParameterAdded,
    };
    const actionInputsDependencies: ActionInputsDependencies = processType === ProcessType.ProcessTemplate ? processTemplateActionInputsDependencies : processType === ProcessType.ProjectTemplate ? projectTemplateActionInputsDependencies : projectActionInputsDependencies;
    return (<>
            <ActionPropertiesEditor actionInputsDependencies={actionInputsDependencies} inputDependencies={inputDependencies} plugin={props.plugin} editor={props.editor} inputs={props.inputs} properties={props.properties} packages={props.packages} gitDependencies={props.gitDependencies} doBusyTask={props.doBusyTask} busy={props.busy} runOn={props.runOn} setInputs={props.setInputs} setProperties={props.setProperties} setPackages={props.setPackages} setGitDependencies={props.setGitDependencies} additionalActions={props.additionalActions} getFieldError={props.getFieldError} errors={props.errors} expandedByDefault={props.expandedByDefault} refreshRunOn={props.refreshRunOn} getProcessResource={props.getProcessResource} isNew={props.isNew}/>

            {props.actionHasFeatures && (<FeatureEditor actionInputsDependencies={actionInputsDependencies} plugin={props.plugin} projectId={props.projectId} gitRef={props.gitRefResource} isNew={props.isNew} doBusyTask={props.doBusyTask} busy={props.busy} properties={props.properties} gitDependencies={props.gitDependencies} packages={props.packages} runOn={props.runOn} setProperties={props.setProperties} setPackages={props.setPackages} setGitDependencies={props.setGitDependencies} enabledFeatures={props.enabledFeatures} getFieldError={props.getFieldError} errors={props.errors} expandedByDefault={props.expandedByDefault} openFeaturesElement={props.openFeaturesElement} refreshRunOn={props.refreshRunOn} localNames={localNames}/>)}
        </>);
}
